import { useEffect, useState } from "react"
import { api } from "../../../../../env"
import { errMessage } from "../../../../../utils/errors/errorHandler"
import LoadingButton from "../../../../../components/LoadingButton"
import ExpandingTextArea from "../../../../../components/ExpandingTextArea"
import { LoadingSpinner } from "../../../../../components/loading"
import ErrorFrame from "../../../../../components/error"
import { AreaField, CountStepper, ModeTabs, TextField } from "../fields"
import QaReviewPanel, { InlineChange } from "./QaReviewPanel"
import { useQaReview } from "./useQaReview"

const emptyChapter = () => ({ title: "", theme: "", description: "", prompt: "", activities: [], sparks: [] })
const emptyActivity = () => ({ title: "", description: "" })
const emptySpark = () => ({ name: "", displayName: "", description: "", content: "" })

// The fixed spark order (and activity order) the track follows; used to label each item's intent
// and to tell the transcript generator what kind of spark it is writing.
const SPARK_TYPES = ["Awareness", "Capability", "Action"]
const ACTIVITY_TYPES = ["Insight", "Application", "Transformation"]

const combinePrompt = (...parts) => parts.map(s => (s ?? "").trim()).filter(Boolean).join("\n\n")

// How often the client polls the draft for server-reported generation progress.
const POLL_INTERVAL_MS = 2000

export default function ChaptersContent({ value, onChange, blocks, dna, trackId, genRunning, setGenRunning, flushSave, onBack, onNext }) {

    // Server-reported generation progress; the bulk run is orchestrated on the backend and observed
    // by polling (see the poll effect below). null until a run is observed.
    const [generation, setGeneration] = useState(null)
    // The bulk "Generate" is in flight (server-side) exactly while genRunning is true.
    const fillingAll = genRunning
    const fillTotal = generation?.total ?? 0
    const fillDone = generation?.done ?? 0
    // Tracks which chapters are EXPANDED; everything starts collapsed (empty set), so chapters are
    // collapsed both on first render and as new ones arrive (Fill everything, saved from the composer).
    const [expanded, setExpanded] = useState(() => new Set())
    const [error, setError] = useState("")
    // Index of the chapter currently awaiting delete confirmation (or null).
    const [confirmDeleteChapter, setConfirmDeleteChapter] = useState(null)
    // The in-progress chapter being composed. It lives OUTSIDE `chapters` so the list only ever holds
    // complete, saved chapters — nothing empty can be added. null = no composer open.
    // Shape: { mode: 'manual' | 'ai', chapter, prompt, generating }
    const [chapterDraft, setChapterDraft] = useState(null)
    // Author-chosen counts that drive "Generate": exact chapters, and activities/sparks per chapter.
    // Chapter default mirrors the backend's duration→count mapping, capped at the input max of 10.
    const [chapterCount, setChapterCount] = useState(() => Math.min(10, ({ 1: 3, 3: 5, 6: 8, 12: 10 })[blocks?.durationMonths] ?? 5))
    const [activityCount, setActivityCount] = useState(3)
    const [sparkCount, setSparkCount] = useState(3)

    // Cursor-style AI review queue. Owns the QA report + the one-at-a-time accept/decline/skip flow;
    // the active change's diff renders inline at its chapter/activity/spark (see InlineChange below).
    const review = useQaReview({ blocks, dna, chapters: value ?? [], onChaptersChange: onChange, trackId })
    const reviewActive = review.active
    const activeChapterIdx = reviewActive?.chapterIndex
    // When a change becomes active, expand its chapter so its inline diff is visible. The active
    // InlineChange scrolls itself into view (see QaReviewPanel), so navigation lands precisely on it.
    useEffect(() => {
        if (activeChapterIdx == null) return
        setExpanded(prev => prev.has(activeChapterIdx) ? prev : new Set(prev).add(activeChapterIdx))
    }, [activeChapterIdx, reviewActive?.id])

    const toggleCollapsed = (idx) => {
        // Drop any pending delete confirmation for this card when it collapses, so it can't linger.
        setConfirmDeleteChapter(prev => prev === idx ? null : prev)
        setExpanded(prev => {
            const next = new Set(prev)
            next.has(idx) ? next.delete(idx) : next.add(idx)
            return next
        })
    }

    // Indices of chapters that actually render a collapse toggle (not loading/failed placeholder cards).
    const collapsibleIdxs = (value ?? []).reduce((acc, c, i) => (!c.__status ? [...acc, i] : acc), [])
    const allCollapsed = collapsibleIdxs.length > 0 && collapsibleIdxs.every(i => !expanded.has(i))
    const toggleAll = () => setExpanded(allCollapsed ? new Set(collapsibleIdxs) : new Set())

    const chapters = value ?? []
    const updateChapter = (idx, chapter) => onChange(chapters.map((c, i) => i === idx ? chapter : c))
    const setField = (idx, field, v) => updateChapter(idx, { ...chapters[idx], [field]: v })
    const removeChapter = (idx) => onChange(chapters.filter((_, i) => i !== idx))

    // Live-generation view helpers. During a fresh run's "outline" phase the server has a target
    // count but no chapter cards yet (~10-30s while the outline is written), so we render skeletons
    // + a banner — the user always sees progress instead of an unchanged screen. Once the server
    // writes real "loading" placeholders, the per-chapter render below takes over (skeletons hide).
    const hasLoadingChapter = chapters.some(c => c.__status === "loading")
    const completedCount = chapters.filter(c => !c.__status).length
    const skeletonCount = fillTotal > 0 ? fillTotal : Math.max(1, chapterCount - completedCount)
    const genBannerText = !hasLoadingChapter
        ? "Planning your chapters…"
        : (fillTotal > 0 ? `Writing your chapters… ${fillDone} of ${fillTotal} done` : "Writing your chapters…")

    // --- Compose-then-save: a chapter is built in `chapterDraft` and only joins `chapters` on Save. ---
    // Open the composer in the requested mode (no chapter enters the list yet).
    const openChapterDraft = (mode) => setChapterDraft({ mode, chapter: emptyChapter(), prompt: "", generating: false, activityCount: 3, sparkCount: 3 })
    // Toggle the open composer's mode, preserving whatever has been filled in so far.
    const setChapterMode = (mode) => setChapterDraft(d => ({ ...d, mode }))
    // Title-only validation, matching the per-item composer.
    const chapterCommitOk = chapterDraft && !!chapterDraft.chapter.title?.trim()
    // In AI mode the editable fields appear once generation has produced content, so the result can be
    // reviewed/edited before saving. In manual mode they're always shown. The `typeof v === "string"`
    // guard skips the activities/sparks arrays; prompt is kept out of the chapter until Save.
    const chapterHasContent = chapterDraft && Object.values(chapterDraft.chapter).some(v => typeof v === "string" && v.trim().length > 0)
    const saveChapterDraft = () => {
        if (!chapterCommitOk) return
        // Commit the prompt onto the chapter (it feeds per-item generation as standing context).
        // The new chapter is appended at the end, so its index is the current length. Add that index
        // to `expanded` so it opens expanded — the author can see its insides and add activities/sparks.
        const newIdx = chapters.length
        onChange([...chapters, { ...chapterDraft.chapter, prompt: chapterDraft.prompt ?? "" }])
        setExpanded(prev => new Set(prev).add(newIdx))
        setChapterDraft(null)
    }

    const mapGenerated = (data) => ({
        activities: Array.isArray(data?.activities) ? data.activities.map(a => ({ title: a?.title ?? "", description: a?.description ?? "" })) : [],
        sparks: Array.isArray(data?.sparks) ? data.sparks.map(s => ({ name: s?.name ?? "", displayName: s?.displayName ?? "", description: s?.description ?? "", content: s?.content ?? "" })) : [],
    })

    // Sibling-chapter outline, for "don't repeat the other chapters" context.
    const outlineCtx = () => chapters.map(c => ({ title: c.title, theme: c.theme, description: c.description })).filter(c => c.title)

    // Light "previous knowledge" context for per-item generation: sibling outline + the titles
    // already in this chapter (so the model adds new ground, not a repeat) + author guidance.
    const itemContext = (chapter, promptText) => ({
        blocks, dna,
        chapter: { title: chapter.title, theme: chapter.theme, description: chapter.description },
        outline: outlineCtx(),
        existingActivities: (chapter.activities ?? []).map(a => ({ title: a.title })).filter(a => a.title),
        existingSparks: (chapter.sparks ?? []).map(s => ({ title: s.displayName, description: s.description })).filter(s => s.title),
        prompt: combinePrompt(chapter.prompt, promptText),
    })

    const requireTitle = (chapter) => {
        if (!chapter.title || chapter.title.trim().length === 0) {
            setError("Give the chapter a title before generating its content."); return false
        }
        return true
    }

    // --- "Generate with AI" inside the composer: fills the draft's title/theme/desc + activities/sparks. ---
    // The prompt stays in `draft.prompt` (separate from `draft.chapter`) until Save, so `chapterHasContent`
    // stays false until generation actually fills the text fields. Generated activities/sparks ride along
    // in the draft and become editable once the chapter is saved and expanded.
    const generateChapterDraft = () => {
        setError("")
        setChapterDraft(d => ({ ...d, generating: true }))
        api.post("tracks/generate/chapter", { blocks, dna, outline: outlineCtx(), prompt: chapterDraft.prompt ?? "", activityCount: chapterDraft.activityCount, sparkCount: chapterDraft.sparkCount })
            .then(res => {
                const d = res.data ?? {}
                setChapterDraft(prev => ({
                    ...prev, generating: false,
                    chapter: { ...emptyChapter(), title: d.title ?? "", theme: d.theme ?? "", description: d.description ?? "", ...mapGenerated(d) },
                }))
            })
            .catch(e => {
                setError(errMessage(e, "Couldn't generate this chapter. Please try again."))
                setChapterDraft(d => ({ ...d, generating: false }))
            })
    }

    // --- Per-item generation: each returns a promise of the generated fields for the prompt card. ---

    const onGenerateActivity = (idx) => (promptText) => {
        const chapter = chapters[idx]
        if (!requireTitle(chapter)) return Promise.reject(new Error("no-title"))
        setError("")
        return api.post("tracks/generate/activity", itemContext(chapter, promptText))
            .then(res => ({ title: res.data?.title ?? "", description: res.data?.description ?? "" }))
    }

    const onGenerateSpark = (idx) => (promptText) => {
        const chapter = chapters[idx]
        if (!requireTitle(chapter)) return Promise.reject(new Error("no-title"))
        setError("")
        return api.post("tracks/generate/spark", itemContext(chapter, promptText))
            .then(res => ({ name: res.data?.name ?? "", displayName: res.data?.displayName ?? "", description: res.data?.description ?? "", content: res.data?.content ?? "" }))
    }

    // Request the full transcript for one spark. Resolves to the markdown content string.
    const requestTranscript = (chapter, spark, type) =>
        api.post("tracks/generate/spark-transcript", {
            blocks, dna,
            chapter: { title: chapter.title, theme: chapter.theme, description: chapter.description },
            spark: { type, displayName: spark.displayName, description: spark.description },
        }).then(res => res.data?.content ?? "")

    // While a server run is live, poll the draft and render chapters + progress from the persisted
    // buildState. The server owns buildState.chapters/generation for the duration, so we mirror them
    // into the wizard (autosave is bailed in BuildTrack while genRunning). Stops on a terminal status,
    // unmount, or 3 consecutive failed polls.
    useEffect(() => {
        if (!genRunning || !trackId) return
        let cancelled = false
        let fails = 0
        // Whether we've yet observed a live generation in the persisted buildState. The server seeds
        // it slightly AFTER this effect's first poll fires (the POST that seeds it is still in
        // flight), so until we've seen it, a null generation means "seed not visible yet" — NOT
        // "run finished". Seed from current state so a re-mount mid-run is covered immediately.
        let sawGen = generation != null
        const startedAt = Date.now()
        // Watchdog: if the server's heartbeat (generation.updatedAt) stops advancing for >90s the
        // run has died (e.g. server restart). Stop polling so the user can retry, rather than spin
        // forever on a frozen "running" state.
        let lastStamp = null
        let lastChangeAt = Date.now()
        const tick = () => api.get(`tracks/${trackId}`)
            .then(res => {
                if (cancelled) return
                fails = 0
                const bs = res.data?.buildState
                const gen = bs?.generation ?? null
                if (gen) {
                    sawGen = true
                    setGeneration(gen)
                    // Only mirror server chapters once a generation exists; a startup-null tick would
                    // otherwise overwrite local chapters with the stale, pre-seed flushSave snapshot.
                    if (Array.isArray(bs?.chapters)) onChange(bs.chapters)
                    const stamp = gen.updatedAt ?? null
                    if (stamp !== lastStamp) { lastStamp = stamp; lastChangeAt = Date.now() }
                    if (gen.status === "done" || gen.status === "failed") {
                        if (gen.status === "failed" && gen.error) setError(gen.error)
                        setGenRunning(false)
                    } else if (Date.now() - lastChangeAt > 90000) {
                        setError("Generation stalled. Reopen the draft to resume.")
                        setGenRunning(false)
                    }
                } else if (sawGen) {
                    // The run finished and its generation marker was cleared — we're done.
                    setGenRunning(false)
                } else if (Date.now() - startedAt > 40000) {
                    // Startup window elapsed without the seed ever appearing — the run never started.
                    setError("Couldn't start generating the track. Please try again.")
                    setGenRunning(false)
                }
                // else: still in the startup window with no seed yet — keep polling.
            })
            .catch(() => {
                if (cancelled) return
                if (++fails >= 3) {
                    setError("Lost contact while generating. Reopen the draft to resume.")
                    setGenRunning(false)
                }
            })
        const id = setInterval(tick, POLL_INTERVAL_MS)
        tick()
        return () => { cancelled = true; clearInterval(id) }
    }, [genRunning, trackId]) // eslint-disable-line react-hooks/exhaustive-deps

    // "Generate": the single self-completing action, now orchestrated server-side. Flush the wizard
    // state so the server reads a consistent buildState, then kick ONE request; the poll effect
    // observes progress. The Chapters stepper is the TARGET TOTAL; on a fresh run the server creates
    // (count - existing) new chapters. If any chapter is mid-flight/failed the server resumes those
    // instead, so this also serves as the batch retry.
    const startServerGeneration = async () => {
        setError("")
        try {
            await flushSave()
            setGenRunning(true)
            // suppressErrorToast: a 409 here just means a run is already live; we resume polling
            // below instead of surfacing it as an error (see the catch). See env.js interceptor.
            const { data } = await api.post(`tracks/${trackId}/generate`, { chapterCount, activityCount, sparkCount }, { suppressErrorToast: true })
            setGeneration(data)
        } catch (e) {
            // A run is already live (e.g. another tab) — just start observing it.
            if (e?.response?.status === 409) { setGenRunning(true); return }
            setGenRunning(false)
            setError(errMessage(e, "Couldn't start generating the track. Please try again."))
        }
    }

    const generate = () => {
        if (fillingAll) return
        const existing = value ?? []
        // Mirror the server: a fresh run needs room for new chapters; if some are failed/loading the
        // server resumes those regardless of the count.
        const pending = existing.filter(c => c.__status === "failed" || c.__status === "loading")
        if (pending.length === 0) {
            const completed = existing.filter(c => !c.__status)
            if (chapterCount - completed.length <= 0) {
                setError(`This track already has ${completed.length} ${completed.length === 1 ? "chapter" : "chapters"}. Increase the chapter count or remove some to generate more.`)
                return
            }
        }
        startServerGeneration()
    }

    // Retry a failed chapter. The server resume path reprocesses every failed/loading placeholder, so
    // this re-kicks the same one-request generation.
    const retryFill = () => {
        if (fillingAll) return
        startServerGeneration()
    }

    const handleNext = () => {
        if (chapters.length === 0) { setError("Add at least one chapter before continuing."); return }
        if (chapters.some(c => c.__status === "loading")) { setError("Some chapters are still generating. Wait for them to finish before continuing."); return }
        if (chapters.some(c => c.__status === "failed")) { setError("Some chapters failed to generate. Retry or remove them before continuing."); return }
        if (chapters.some(c => !c.title || c.title.trim().length === 0)) { setError("Every chapter needs a title. Generate or fill in any draft chapters first."); return }
        setError("")
        onNext()
    }

    return (
        <div className="card standard-padding flex columns gap-l showing">
            <div className="flex columns gap-s">
                <div className="flex space-between center gap-xs wrap">
                    <div className="flex columns gap-xxs">
                        <p className="card-title">Chapters & Content</p>
                        <p className="card-body neutral">Set how much to generate, then let MentorMatches write the Activities and Sparks for each chapter.</p>
                    </div>
                    <div className="flex center gap-xs">
                        {collapsibleIdxs.length > 0 && (
                            <button className="text with-icon leading" onClick={toggleAll}>
                                <span className="material-symbols-outlined scale-xs">{allCollapsed ? "unfold_more" : "unfold_less"}</span>
                                <span>{allCollapsed ? "Expand all" : "Collapse all"}</span>
                            </button>
                        )}
                    </div>
                </div>

                <div className="bt-generate-bar">
                    <div className="bt-recipe">
                        <CountStepper icon="menu_book" label="Chapters" value={chapterCount} onChange={setChapterCount} min={1} max={10} disabled={fillingAll} />
                        <CountStepper icon="checklist" label="Activities each" value={activityCount} onChange={setActivityCount} min={1} max={6} disabled={fillingAll} />
                        <CountStepper icon="bolt" label="Sparks each" value={sparkCount} onChange={setSparkCount} min={1} max={4} disabled={fillingAll} />
                    </div>
                    <div className="flex columns gap-xxs bt-generate-cta">
                        <LoadingButton className="solid-accent bt-generate-btn" label="Generate" leadingIcon="auto_awesome" loading={fillingAll} disabled={fillingAll} onClick={generate} />
                        {fillingAll && fillTotal > 0 && <p className="card-label neutral showing" style={{ textAlign: "right" }}>{fillDone} of {fillTotal} chapters written</p>}
                    </div>
                </div>

                {/* Generating banner: visible the instant a run starts (including the outline phase
                    before any chapter cards exist), so the screen never looks unchanged. */}
                {fillingAll && (
                    <div className="bt-gen-banner showing flex center gap-xs standard-padding-s">
                        <span className="material-symbols-outlined scale-xs spin">progress_activity</span>
                        <span className="card-body medium">{genBannerText}</span>
                    </div>
                )}
            </div>

            {/* Disable all chapter edits while a fill is running: the edit handlers compute from a
                snapshot of `chapters`, so an edit landing mid-fill would otherwise revert a
                just-filled chapter back to a loading placeholder. The header/footer stay enabled. */}
            <fieldset disabled={fillingAll} className="flex columns gap-s" style={{ border: 0, padding: 0, margin: 0, minInlineSize: 0 }}>
            {chapters.map((chapter, idx) => {
              if (chapter.__status === "loading") {
                const perCh = generation?.perChapter?.find(p => p.fillId === chapter.__fillId)
                const label = perCh && perCh.sparksTotal > 0
                    ? `Writing this chapter… ${perCh.sparksDone} of ${perCh.sparksTotal} transcripts`
                    : "Writing this chapter…"
                return (
                <div key={idx} className="bt-chapter flex columns gap-s showing">
                    <div className="flex space-between center gap-xs">
                        <div className="flex center gap-xs">
                            <span className="bt-step-num">{idx + 1}</span>
                            <p className="card-title">{chapter.title || "Untitled chapter"}</p>
                        </div>
                    </div>
                    <LoadingSpinner className="flex columns center gap-xs standard-padding-s" label={label} />
                </div>
                )
              }
              if (chapter.__status === "failed") return (
                <div key={idx} className="bt-chapter flex columns gap-s showing">
                    <div className="flex space-between center gap-xs">
                        <div className="flex center gap-xs">
                            <span className="bt-step-num">{idx + 1}</span>
                            <p className="card-title">{chapter.title || "Untitled chapter"}</p>
                        </div>
                        <button className="icon outline small" onClick={() => removeChapter(idx)}><span className="material-symbols-outlined scale-xs">delete</span></button>
                    </div>
                    <ErrorFrame message="This chapter couldn't be generated." action="Retry" onAction={() => retryFill()} />
                </div>
              )
              const isCollapsed = !expanded.has(idx)
              const aCount = chapter.activities?.length ?? 0
              const sCount = chapter.sparks?.length ?? 0
              const chapterDec = reviewActive && reviewActive.itemType === "chapter" && reviewActive.chapterIndex === idx ? reviewActive : null
              return (
                <div key={idx} className="bt-chapter flex columns gap-s">
                    <div className="flex space-between center gap-xs">
                        <div className="flex center gap-xs">
                            <span className="bt-step-num">{idx + 1}</span>
                            <p className="card-title">{chapter.title || "Untitled chapter"}</p>
                        </div>
                        <div className="flex center gap-xs">
                            <button className="icon outline small" onClick={() => toggleCollapsed(idx)}>
                                <span className="material-symbols-outlined scale-xs">{isCollapsed ? "keyboard_arrow_down" : "keyboard_arrow_up"}</span>
                            </button>
                        </div>
                    </div>

                    {isCollapsed ? (
                        <p className="card-body neutral">{aCount} {aCount === 1 ? "activity" : "activities"} · {sCount} {sCount === 1 ? "spark" : "sparks"}</p>
                    ) : (<>
                    {chapterDec && <InlineChange change={chapterDec} onAccept={review.accept} onDecline={review.decline} onSkip={review.skip} onRetry={review.retry} />}
                    <TextField label="Title" value={chapter.title} onChange={v => setField(idx, "title", v)} placeholder="Chapter title" />
                    <TextField label="Theme" value={chapter.theme} onChange={v => setField(idx, "theme", v)} placeholder="Short theme label" />
                    <AreaField label="Description" value={chapter.description} onChange={v => setField(idx, "description", v)} placeholder="What this chapter covers" minRows={2} maxRows={4} />

                    <ItemList
                        title="Activities"
                        noun="activity"
                        items={chapter.activities ?? []}
                        onChange={items => setField(idx, "activities", items)}
                        emptyItem={emptyActivity}
                        addLabel="+ Add activity"
                        itemLabel={(it) => it.title || "Untitled activity"}
                        canCommit={(it) => !!it.title?.trim()}
                        onGenerate={onGenerateActivity(idx)}
                        review={review}
                        chapterIndex={idx}
                        itemType="activity"
                        promptPlaceholder="Describe the activity you want — what the mentee should do and produce"
                        render={(item, update, aIdx) => (<>
                            {ACTIVITY_TYPES[aIdx] && <p className="card-label neutral">{ACTIVITY_TYPES[aIdx]}</p>}
                            <TextField label="Title" value={item.title} onChange={v => update({ ...item, title: v })} placeholder="Activity title" />
                            <AreaField label="Description" value={item.description} onChange={v => update({ ...item, description: v })} placeholder="What the mentee should do" minRows={2} maxRows={5} />
                        </>)}
                    />

                    <ItemList
                        title="Sparks"
                        noun="spark"
                        items={chapter.sparks ?? []}
                        onChange={items => setField(idx, "sparks", items)}
                        emptyItem={emptySpark}
                        addLabel="+ Add spark"
                        itemLabel={(it) => it.displayName || "Untitled spark"}
                        canCommit={(it) => !!it.displayName?.trim()}
                        onGenerate={onGenerateSpark(idx)}
                        review={review}
                        chapterIndex={idx}
                        itemType="spark"
                        promptPlaceholder="Describe the spark you want — the one idea it should teach"
                        render={(item, update, sIdx) => (<>
                            <TextField label="Display name" value={item.displayName} onChange={v => update({ ...item, displayName: v, name: item.name || v })} placeholder="Spark name" />
                            <AreaField label="Description" value={item.description} onChange={v => update({ ...item, description: v })} placeholder="One-paragraph summary" minRows={2} maxRows={4} />
                            <div className="flex space-between center gap-xs vi-spacer-xs">
                                <p className="card-label neutral">Transcript{SPARK_TYPES[sIdx] ? ` · ${SPARK_TYPES[sIdx]}` : ""}</p>
                                <TranscriptButton
                                    disabled={!item.displayName?.trim()}
                                    run={() => requestTranscript(chapter, item, SPARK_TYPES[sIdx])
                                        .then(content => update({ ...item, content }))
                                        .catch(e => setError(errMessage(e, "Couldn't write the transcript. Please try again.")))}
                                />
                            </div>
                            <AreaField label="Content (markdown)" value={item.content} onChange={v => update({ ...item, content: v })} placeholder="Full learning material, or use Write full transcript with AI" minRows={4} maxRows={14} />
                        </>)}
                    />

                    <div className="flex justify-end center gap-xs vi-spacer-xs">
                        {confirmDeleteChapter === idx ? (<>
                            <span className="card-label neutral">Delete this chapter?</span>
                            <button className="solid-error small" onClick={() => { removeChapter(idx); setConfirmDeleteChapter(null) }}>Delete</button>
                            <button className="text small" onClick={() => setConfirmDeleteChapter(null)}>Cancel</button>
                        </>) : (
                            <button className="text error small with-icon leading" onClick={() => setConfirmDeleteChapter(idx)}>
                                <span className="material-symbols-outlined scale-xs">delete</span>
                                <span>Delete chapter</span>
                            </button>
                        )}
                    </div>
                    </>)}
                </div>
              )
            })}

            {/* Outline-phase skeletons: the server has a target count but hasn't created chapter
                placeholders yet. Show one greyed card per chapter-to-come so the area isn't blank.
                Hidden as soon as real "loading" placeholders arrive (then the map renders those). */}
            {fillingAll && !hasLoadingChapter && Array.from({ length: skeletonCount }).map((_, i) => (
                <div key={`skeleton-${i}`} className="bt-chapter flex columns gap-s showing">
                    <div className="flex center gap-xs">
                        <span className="bt-step-num">{completedCount + i + 1}</span>
                        <p className="card-title neutral">Planning chapter…</p>
                    </div>
                    <div className="flex columns gap-xs standard-padding-s">
                        <div className="bt-skeleton-bar" style={{ width: "60%" }} />
                        <div className="bt-skeleton-bar" style={{ width: "90%" }} />
                        <div className="bt-skeleton-bar" style={{ width: "75%" }} />
                    </div>
                </div>
            ))}

            {!fillingAll && !chapterDraft && (
                <div className="flex center gap-xs wrap">
                    <button className="dashed-card standard-padding-s card-title neutral" style={{ flex: 1 }} onClick={() => openChapterDraft("manual")}>+ Add chapter</button>
                </div>
            )}

            {chapterDraft && (
                <div className="bt-chapter flex columns gap-s showing">
                    <div className="flex space-between center gap-xs wrap">
                        <div className="flex center gap-xs">
                            <span className="bt-step-num">{chapters.length + 1}</span>
                            <p className="card-title">New chapter</p>
                        </div>
                        <ModeTabs mode={chapterDraft.mode} onChange={setChapterMode} />
                    </div>
                    {chapterDraft.mode === "ai" && (
                        <AiPromptCard
                            value={chapterDraft.prompt}
                            onChange={v => setChapterDraft(d => ({ ...d, prompt: v }))}
                            loading={chapterDraft.generating}
                            onGenerate={generateChapterDraft}
                            placeholder="Describe the chapter you want — its focus and what the mentee should walk away able to do"
                            controls={
                                <div className="bt-recipe">
                                    <CountStepper icon="checklist" label="Activities" value={chapterDraft.activityCount} onChange={v => setChapterDraft(d => ({ ...d, activityCount: v }))} min={1} max={6} disabled={chapterDraft.generating} />
                                    <CountStepper icon="bolt" label="Sparks" value={chapterDraft.sparkCount} onChange={v => setChapterDraft(d => ({ ...d, sparkCount: v }))} min={1} max={4} disabled={chapterDraft.generating} />
                                </div>
                            }
                        />
                    )}
                    {(chapterDraft.mode === "manual" || chapterHasContent) && (<>
                        <TextField label="Title" value={chapterDraft.chapter.title} onChange={v => setChapterDraft(d => ({ ...d, chapter: { ...d.chapter, title: v } }))} placeholder="Chapter title" />
                        <TextField label="Theme" value={chapterDraft.chapter.theme} onChange={v => setChapterDraft(d => ({ ...d, chapter: { ...d.chapter, theme: v } }))} placeholder="Short theme label" />
                        <AreaField label="Description" value={chapterDraft.chapter.description} onChange={v => setChapterDraft(d => ({ ...d, chapter: { ...d.chapter, description: v } }))} placeholder="What this chapter covers" minRows={2} maxRows={4} />
                    </>)}
                    <div className="flex justify-end center gap-xs vi-spacer-xs">
                        <button className="text small" onClick={() => setChapterDraft(null)}>Cancel</button>
                        <button className="solid small" disabled={!chapterCommitOk} onClick={saveChapterDraft}>Save</button>
                    </div>
                </div>
            )}
            </fieldset>

            {/* Validate the track and add/edit/remove items in one click, right here so changes show
                immediately in the chapters above (no round trip to the Review step). Hidden while a
                bulk generation is running or before any real chapter exists. */}
            {!fillingAll && (value ?? []).some(c => !c.__status && c.title?.trim()) && (
                <QaReviewPanel review={review} />
            )}

            {error && <p className="card-label error showing">{error}</p>}

            <div className="flex space-between center gap-xs bt-footer bt-footer-merged">
                <button className="outline with-icon leading" disabled={fillingAll} onClick={onBack}>
                    <span className="material-symbols-outlined scale-xs">chevron_left</span>
                    <span>Back</span>
                </button>
                <button className="solid with-icon trailing" onClick={handleNext}>
                    <span>Review track</span>
                    <span className="material-symbols-outlined scale-xs">chevron_right</span>
                </button>
            </div>
        </div>
    )
}

// A compact card that collects a prompt and generates content, shown in place of an item's fields
// until generation completes (then the parent swaps it for the filled, editable fields).
function AiPromptCard({ value, onChange, loading, onGenerate, placeholder, controls }) {
    return (
        <div className="bt-ai-panel">
            <div className="bt-field">
                <p className="bt-field-label"><span className="bt-field-hint">AI</span>Prompt</p>
                <div className="input-wrapper regular-border bt-prompt-box" style={{ height: "unset", alignItems: "unset" }}>
                    <ExpandingTextArea placeholder={placeholder} dynamicValue={value} onChange={onChange} minRows={2} maxRows={6} />
                </div>
            </div>
            <div className="bt-ai-panel-actions">
                {controls}
                <LoadingButton className="solid-accent bt-generate-btn" label="Generate" leadingIcon="auto_awesome" loading={loading} onClick={onGenerate} />
            </div>
        </div>
    )
}

// "Write full transcript with AI" on a single spark. Manages its own loading; `run` returns a promise.
function TranscriptButton({ run, disabled }) {
    const [loading, setLoading] = useState(false)
    const click = () => { setLoading(true); Promise.resolve(run()).finally(() => setLoading(false)) }
    return <LoadingButton className="outline small" label="Write full transcript with AI" leadingIcon="auto_awesome" loading={loading} disabled={disabled} onClick={click} />
}

function ItemList({ title, noun, items, onChange, emptyItem, addLabel, render, onGenerate, promptPlaceholder, itemLabel, canCommit, review, chapterIndex, itemType }) {
    // Tracks which items are EXPANDED; everything starts collapsed (empty set), except a newly-saved
    // item, which is expanded on save so it's immediately editable (see `save`).
    const [expanded, setExpanded] = useState(() => new Set())

    // The active AI change targeting THIS list, if any: an in-place edit/remove of one item, or an
    // "add" of a brand-new item (rendered as a ghost row at the end).
    const active = review?.active
    const targetsThis = active && active.chapterIndex === chapterIndex && active.itemType === itemType
    const editDecIdx = targetsThis && active.op !== "add" ? active.itemIndex : null
    const addDec = targetsThis && active.op === "add" ? active : null
    // Keep the item under review expanded so its inline diff is visible.
    useEffect(() => {
        if (editDecIdx == null) return
        setExpanded(prev => prev.has(editDecIdx) ? prev : new Set(prev).add(editDecIdx))
    }, [editDecIdx])
    // Index of the item currently awaiting delete confirmation (or null).
    const [confirmDelete, setConfirmDelete] = useState(null)
    // The in-progress item being composed. It lives OUTSIDE `items` so the list only ever holds
    // complete, saved items — nothing empty can be added. null = no composer open.
    // Shape: { mode: 'manual' | 'ai', item, prompt, generating }
    const [draft, setDraft] = useState(null)

    const toggle = (idx) => {
        // Drop any pending delete confirmation for this item when it collapses, so it can't linger.
        setConfirmDelete(prev => prev === idx ? null : prev)
        setExpanded(prev => {
            const next = new Set(prev)
            next.has(idx) ? next.delete(idx) : next.add(idx)
            return next
        })
    }
    const update = (idx, item) => onChange(items.map((it, i) => i === idx ? item : it))
    const remove = (idx) => onChange(items.filter((_, i) => i !== idx))

    // Open the composer in the requested mode (no item enters the list yet).
    const openDraft = (mode) => setDraft({ mode, item: emptyItem(), prompt: "", generating: false })
    // Toggle the open composer's mode, preserving whatever has been filled in so far.
    const setMode = (mode) => setDraft(d => ({ ...d, mode }))

    const generate = () => {
        setDraft(d => ({ ...d, generating: true }))
        onGenerate(draft.prompt ?? "")
            .then(fields => setDraft(d => ({ ...d, generating: false, item: { ...emptyItem(), ...fields } })))
            .catch(() => setDraft(d => ({ ...d, generating: false })))
    }

    const commitOk = draft && (canCommit ? canCommit(draft.item) : true)
    const save = () => {
        if (!commitOk) return
        // Appended at the end, so the new item's index is the current length. Expand it so it opens
        // ready to edit.
        const newIdx = items.length
        onChange([...items, draft.item])
        setExpanded(prev => new Set(prev).add(newIdx))
        setDraft(null)
    }

    // In AI mode the editable fields appear once generation has produced content, so the result
    // can be reviewed/edited before saving. In manual mode they're always shown.
    const hasContent = draft && Object.values(draft.item).some(v => typeof v === "string" && v.trim().length > 0)

    return (
        <div className="flex columns gap-xs vi-spacer-xs">
            <div className="flex space-between center gap-xs">
                <p className="card-body medium">{title}</p>
                {!draft && (
                    <button className="text" onClick={() => openDraft("manual")}>{addLabel}</button>
                )}
            </div>
            {items.length === 0 && !draft && !addDec && <p className="card-body neutral">None yet.</p>}
            {items.map((item, idx) => {
              const isCollapsed = !expanded.has(idx)
              const label = itemLabel ? itemLabel(item) : (item.title || item.displayName || item.name || "Untitled")
              const dec = editDecIdx === idx ? active : null
              return (
                <div key={idx} className="bt-subitem" style={dec && dec.op === "remove" ? { background: "rgba(179, 38, 30, 0.04)" } : undefined}>
                    <div className="flex space-between center gap-xs">
                        <p className="card-body medium">{isCollapsed ? label : ""}</p>
                        <div className="flex center gap-xs">
                            <button className="icon outline small" onClick={() => toggle(idx)}><span className="material-symbols-outlined scale-xs">{isCollapsed ? "keyboard_arrow_down" : "keyboard_arrow_up"}</span></button>
                        </div>
                    </div>
                    {!isCollapsed && (<>
                        {dec && <InlineChange change={dec} onAccept={review.accept} onDecline={review.decline} onSkip={review.skip} onRetry={review.retry} />}
                        {render(item, (updated) => update(idx, updated), idx)}
                        <div className="flex justify-end center gap-xs vi-spacer-xs">
                            {confirmDelete === idx ? (<>
                                <span className="card-label neutral">Remove this item?</span>
                                <button className="solid-error small" onClick={() => { remove(idx); setConfirmDelete(null) }}>Delete</button>
                                <button className="text small" onClick={() => setConfirmDelete(null)}>Cancel</button>
                            </>) : (
                                <button className="text error small with-icon leading" onClick={() => setConfirmDelete(idx)}>
                                    <span className="material-symbols-outlined scale-xs">delete</span>
                                    <span>Delete</span>
                                </button>
                            )}
                        </div>
                    </>)}
                </div>
              )
            })}

            {addDec && (
                <div className="bt-subitem flex columns gap-xs" style={{ background: "rgba(26, 127, 68, 0.04)" }}>
                    <InlineChange change={addDec} onAccept={review.accept} onDecline={review.decline} onSkip={review.skip} onRetry={review.retry} />
                </div>
            )}

            {draft && (
                <div className="bt-subitem flex columns gap-xs">
                    <div className="flex space-between center gap-xs wrap">
                        <p className="card-body medium">New {noun}</p>
                        {onGenerate && <ModeTabs mode={draft.mode} onChange={setMode} />}
                    </div>
                    {draft.mode === "ai" && (
                        <AiPromptCard
                            value={draft.prompt}
                            onChange={v => setDraft(d => ({ ...d, prompt: v }))}
                            loading={draft.generating}
                            onGenerate={generate}
                            placeholder={promptPlaceholder}
                        />
                    )}
                    {(draft.mode === "manual" || hasContent) && render(draft.item, (updated) => setDraft(d => ({ ...d, item: updated })), items.length)}
                    <div className="flex justify-end center gap-xs vi-spacer-xs">
                        <button className="text small" onClick={() => setDraft(null)}>Cancel</button>
                        <button className="solid small" disabled={!commitOk} onClick={save}>Save</button>
                    </div>
                </div>
            )}
        </div>
    )
}
